import Autocomplete from '@mui/material/Autocomplete';
import Box from '@mui/material/Box';
import CircularProgress from '@mui/material/CircularProgress';
import Divider from '@mui/material/Divider';
import Grid from '@mui/material/Grid';
import MenuItem from '@mui/material/MenuItem';
import OutlinedInput from '@mui/material/OutlinedInput';
import Select, { SelectChangeEvent } from '@mui/material/Select';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs, { Dayjs } from 'dayjs';
import { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { isEmpty } from 'src/common/helper';
import BtnGradient from 'src/components/common/button/BtnGradient';
import BtnOutlineGradient from 'src/components/common/button/BtnOutlineGradient';
import { useNotify } from 'src/context/ContextNotify';
import { getAPI, postAPI } from 'src/utils/api';
import { getToken } from 'src/utils/auth';

const inputStyle = {
  fontSize: '14px',
  my: '8px',
  '& .MuiOutlinedInput-notchedOutline': {
    borderColor: '#BDBDBD',
  },
  '& .MuiInputBase-input': { padding: '8px 14px' },
  '& input::placeholder': {
    fontSize: '14px',
    fontWeight: 500,
    color: '#CCD1D9',
    opacity: 1,
  },
  backgroundColor: 'primary.light',
  width: '100%',
};

const defaultError = {
  task: true,
  persons: true,
  patientid: true,
  status: true,
};

const Create = () => {
  const notifyContext = useNotify();
  const [searchParams] = useSearchParams();
  // const navigate = useNavigate()
  const taskid = searchParams.get('id') || '';
  const [statuses, setStatuses] = useState([]);
  const [users, setUsers] = useState([]);
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [patients, setPatients] = useState([]);
  const [caseids, setCaseids] = useState([]);
  const [errors, setErrors] = useState(defaultError);
  const [personsEdit, setPersonsEdit] = useState([]);
  const [patientsEdit, setPatientsEdit] = useState([]);
  const [data, setData] = useState({
    task: '',
    persons: [],
    patientid: '',
    status: '1',
    note: '',
    caseid: '0',
  });

  const autoCompleteStyle = {
    flex: 1,
    backgroundColor: 'primary.light',
    '&.MuiAutocomplete-root .MuiOutlinedInput-root': {
      py: '7px',
    },
    '&.MuiAutocomplete-root .MuiOutlinedInput-root .MuiAutocomplete-input': {
      py: 0,
      fontSize: '14px',
    },
    '& .MuiOutlinedInput-notchedOutline': {
      borderColor: !errors.persons ? '#d32f2f' : '',
    },
  };

  const [deadline, setDeadline] = useState<Dayjs | null>(dayjs());

  const loading = open && patients.length === 0;

  const getTaskDetail = () => {
    const token = getToken();
    getAPI(`/patient/task-view?id=${taskid || ''}&token=${token}`).then(
      (res) => {
        if (res.code === 200) {
          setStatuses(res.data.status);
          setUsers(res.data.users);
          setCaseids(res.data.parts);
          if (taskid) {
            setData({
              task: res.data.taskdata.task,
              persons: res.data.taskdata.persons,
              patientid: res.data.taskdata.patientid,
              status: res.data.taskdata.status,
              note: res.data.taskdata.note,
              caseid: res.data.taskdata.caseid,
            });
            const defaultPersons = res.data.users.filter((user: any) =>
              res.data.taskdata.persons.includes(user.userid)
            );
            setPatientsEdit(res.data.patients);
            setPersonsEdit(defaultPersons);
            setDeadline(dayjs(new Date(res.data.taskdata.deadline)));
          }
        }
      }
    );
  };

  useEffect(() => {
    getTaskDetail();
    // eslint-disable-next-line
  }, []);

  const handleInputChange = (
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const { name, value } = event.target;
    setData({
      ...data,
      [name]: value,
    });
  };
  const handleChangeFilter = (event: SelectChangeEvent) => {
    const { name, value } = event.target;
    setData({
      ...data,
      [name]: value,
    });
  };

  const validateDate = () => {
    if (isEmpty(data.task)) {
      setErrors({ ...defaultError, task: false });
      return false;
    }
    if (data.persons.length === 0) {
      setErrors({ ...defaultError, persons: false });
      return false;
    }
    if (isEmpty(data.patientid)) {
      setErrors({ ...defaultError, patientid: false });
      return false;
    }
    if (isEmpty(data.status)) {
      setErrors({ ...defaultError, status: false });
      return false;
    }
    return true;
  };

  const handleSave = () => {
    if (!validateDate()) {
      return;
    }
    const token = getToken();
    postAPI(`/patient/task-save?token=${token}`, {
      ...data,
      deadline: dayjs(deadline).format('YYYY/MM/DD'),
      id: taskid,
    })
      .then((res) => {
        if (res.code === 200) {
          notifyContext.setNotifyMessage({
            show: true,
            message: '保存出来ました。',
            type: 'success',
          });
          navigate('/patient/task');
        }
        if (res.code === 500 || res.code === 501) {
          notifyContext.setNotifyMessage({
            show: true,
            message: res.msg || 'Failed to post data',
            type: 'error',
          });
        }
      })
      .finally(() => {
        // setCheckAPI(true);
      });
  };
  const handleCancel = () => {
    navigate('/patient/task');
  };

  useEffect(() => {
    if (!open) {
      setPatients([]);
    }
  }, [open]);

  const onPatientChange = (e: any, value: any) => {
    setData({
      ...data,
      patientid: value?.id || '',
    });
  };

  const onInputPatientChange = (e: any, value: any) => {
    const token = getToken();
    getAPI(`/master/suggest?type=patient&keyword=${value}&token=${token}`).then(
      (res) => {
        if (res.code === 200) {
          setPatients(res.data.result);
        }
      }
    );
  };

  const handleUsersChange = (event: any, value: any) => {
    const pers = value.map((val: any) => val.userid);
    setData({
      ...data,
      persons: pers,
    });
  };

  const handleDateChange = (value: any) => {
    setDeadline(value);
  };

  return (
    <Stack>
      <Stack
        width={'785px'}
        sx={{ backgroundColor: 'primary.light' }}
        borderRadius={'4px'}
      >
        <Grid container spacing={3}>
          <Grid item xs={3}>
            <Stack p={'14px 26px'} direction={'row'}>
              <Typography fontWeight={700}>タスク名</Typography>
              <Typography color={'#ED5565'}>*</Typography>
            </Stack>
          </Grid>
          <Grid item xs>
            <Box p={'8px'}>
              <OutlinedInput
                error={!errors.task}
                placeholder={'入力してください'}
                sx={inputStyle}
                inputProps={{
                  sx: {
                    backgroundColor: 'primary.light',
                  },
                }}
                name='task'
                value={data.task}
                onChange={(event) => handleInputChange(event)}
              />
            </Box>
          </Grid>
        </Grid>
        <Divider sx={{ borderColor: '#F3F3F3' }} />
        <Grid container spacing={3}>
          <Grid item xs={3}>
            <Stack p={'14px 26px'} direction={'row'}>
              <Typography fontWeight={700}>担当者</Typography>
              <Typography color={'#ED5565'}>*</Typography>
            </Stack>
          </Grid>
          <Grid item xs>
            <Box p={'8px'}>
              {taskid ? (
                users.length > 0 && (
                  <Autocomplete
                    multiple
                    id='tags-outlined'
                    options={users}
                    getOptionLabel={(option: any) => option.name}
                    isOptionEqualToValue={(option: any, value: any) =>
                      option.userid === value.userid
                    }
                    defaultValue={personsEdit}
                    filterSelectedOptions
                    onChange={handleUsersChange}
                    renderInput={(params) => (
                      <TextField {...params} label='' placeholder='担当者名' />
                    )}
                    sx={autoCompleteStyle}
                  />
                )
              ) : (
                <Autocomplete
                  multiple
                  id='tags-outlined-2'
                  options={users}
                  getOptionLabel={(option: any) => option.name}
                  filterSelectedOptions
                  onChange={handleUsersChange}
                  renderInput={(params) => (
                    <TextField {...params} label='' placeholder='担当者名' />
                  )}
                  sx={autoCompleteStyle}
                />
              )}
            </Box>
          </Grid>
        </Grid>
        <Divider sx={{ borderColor: '#F3F3F3' }} />
        {/* <Grid container spacing={3}>
          <Grid item xs={3}>
            <Stack p={'14px 26px'} direction={'row'}>
              <Typography fontWeight={700}>
                患者 ID
              </Typography>
              <Typography color={'#ED5565'}>*</Typography>
            </Stack>
          </Grid>
          <Grid item xs>
            <Box p={'8px'}>
              <OutlinedInput
                placeholder={'7ケタの数字で入力してください'}
                sx={inputStyle}
                inputProps={{
                  sx: {
                    backgroundColor: 'primary.light',
                  },
                }}
                name='taskname'
                value={data.taskname}
                onChange={(event) => handleInputChange(event)}
                endAdornment={
                  <InputAdornment position='end'>
                    <IconButton
                      aria-label='toggle password visibility'
                      edge='end'
                    >
                      <Box
                        component={'img'}
                        src='/images/icons/ic-search.svg'
                        alt='edit'
                        sx={{ cursor: 'pointer' }}
                        width={'20px'}
                      />
                    </IconButton>
                  </InputAdornment>
                }
              />
            </Box>
          </Grid>
        </Grid>
        <Divider sx={{ borderColor: '#F3F3F3' }} /> */}
        <Grid container spacing={3}>
          <Grid item xs={3}>
            <Stack p={'14px 26px'} direction={'row'}>
              <Typography fontWeight={700}>患者名</Typography>
              <Typography color={'#ED5565'}>*</Typography>
            </Stack>
          </Grid>
          <Grid item xs>
            <Box p={'8px'}>
              {taskid ? (
                <Select
                  value={data.patientid}
                  sx={{
                    height: '32px',
                    '& .MuiSelect-select.MuiSelect-outlined': {
                      py: '5px',
                      backgroundColor: 'primary.light',
                      fontSize: '14px',
                    },
                    flex: 1,
                    width: '100%',
                  }}
                >
                  {patientsEdit.map((pat: any, index) => {
                    return (
                      <MenuItem key={index} value={pat.id}>
                        {pat.fullname}
                      </MenuItem>
                    );
                  })}
                </Select>
              ) : (
                <Autocomplete
                  id='asynchronous-demo'
                  sx={{
                    flex: 1,
                    height: '32px',
                    backgroundColor: 'primary.light',
                    '&.MuiAutocomplete-root .MuiOutlinedInput-root': {
                      py: '7px',
                    },
                    '&.MuiAutocomplete-root .MuiOutlinedInput-root .MuiAutocomplete-input':
                      {
                        py: 0,
                        fontSize: '14px',
                      },
                    '& .MuiOutlinedInput-notchedOutline': {
                      borderColor: !errors.patientid ? '#d32f2f' : '',
                    },
                  }}
                  open={open}
                  onOpen={() => {
                    setOpen(true);
                  }}
                  onClose={() => {
                    setOpen(false);
                  }}
                  isOptionEqualToValue={(patient: any, value) =>
                    patient.fullname === value.fullname
                  }
                  getOptionLabel={(patient) => patient.fullname}
                  options={patients}
                  loading={loading}
                  onChange={onPatientChange}
                  onInputChange={onInputPatientChange}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label=''
                      placeholder='姓　名'
                      InputProps={{
                        ...params.InputProps,
                        endAdornment: (
                          <>
                            {loading ? (
                              <CircularProgress color='inherit' size={20} />
                            ) : null}
                            {params.InputProps.endAdornment}
                          </>
                        ),
                      }}
                    />
                  )}
                />
              )}
            </Box>
          </Grid>
        </Grid>
        <Divider sx={{ borderColor: '#F3F3F3' }} />
        <Grid container spacing={3}>
          <Grid item xs={3}>
            <Stack p={'14px 26px'} direction={'row'}>
              <Typography fontWeight={700}>患者履歴</Typography>
            </Stack>
          </Grid>
          <Grid item xs>
            <Box p={'8px'}>
              <Select
                value={data.caseid}
                name='caseid'
                displayEmpty
                onChange={(e) => handleChangeFilter(e)}
                sx={{
                  height: '32px',
                  '& .MuiSelect-select.MuiSelect-outlined': {
                    py: '5px',
                    color: data.caseid === '' ? '#CCD1D9' : '',
                    backgroundColor: 'primary.light',
                    fontSize: '14px',
                  },
                  flex: 1,
                  width: '100%',
                }}
              >
                <MenuItem value='0' className='placeholder'>
                  入力してください
                </MenuItem>

                {caseids.map((cas: any, index) => {
                  return (
                    <MenuItem key={index} value={cas.id}>
                      {cas.part}
                    </MenuItem>
                  );
                })}
              </Select>
            </Box>
          </Grid>
        </Grid>
        <Divider sx={{ borderColor: '#F3F3F3' }} />
        <Grid container spacing={3}>
          <Grid item xs={3}>
            <Stack p={'14px 26px'} direction={'row'}>
              <Typography fontWeight={700}>期限</Typography>
            </Stack>
          </Grid>
          <Grid item xs>
            <Box p={'8px'}>
              <LocalizationProvider dateAdapter={AdapterDayjs}>
                <DatePicker
                  name='deadline'
                  value={deadline}
                  onChange={(newValue) => handleDateChange(newValue)}
                  format='YYYY/MM/DD'
                  sx={{
                    width: '100%',
                    height: '32px',
                    '& .MuiInputBase-input.MuiOutlinedInput-input': {
                      py: '8px',
                      fontSize: '14px',
                    },
                  }}
                />
              </LocalizationProvider>
            </Box>
          </Grid>
        </Grid>
        <Divider sx={{ borderColor: '#F3F3F3' }} />
        <Grid container spacing={3}>
          <Grid item xs={3}>
            <Stack p={'14px 26px'} direction={'row'}>
              <Typography fontWeight={700}>ステータス</Typography>
              <Typography color={'#ED5565'}>*</Typography>
            </Stack>
          </Grid>
          <Grid item xs>
            <Box p={'8px'}>
              <Select
                value={statuses.length > 0 ? data.status : ''}
                name='status'
                error={!errors.status}
                displayEmpty
                onChange={(e) => handleChangeFilter(e)}
                sx={{
                  height: '32px',
                  '& .MuiSelect-select.MuiSelect-outlined': {
                    py: '5px',
                    // color: data.status === '' ? '#CCD1D9' : '',
                    backgroundColor: 'primary.light',
                    fontSize: '14px',
                  },
                  '& .MuiOutlinedInput-notchedOutline': {
                    borderColor: !errors.status ? '#d32f2f' : '',
                  },
                  flex: 1,
                  width: '100%',
                }}
              >
                {statuses.map((status: any, index) => {
                  return (
                    <MenuItem key={index} value={status.value}>
                      {status.label}
                    </MenuItem>
                  );
                })}
              </Select>
            </Box>
          </Grid>
        </Grid>
        <Divider sx={{ borderColor: '#F3F3F3' }} />
        <Grid container spacing={3}>
          <Grid item xs={3}>
            <Stack p={'14px 26px'} direction={'row'}>
              <Typography fontWeight={700}>備考</Typography>
            </Stack>
          </Grid>
          <Grid item xs>
            <Box p={'8px'}>
              <textarea
                name='note'
                rows={4}
                cols={40}
                value={data.note || ''}
                placeholder='入力してください'
                onChange={(event) => handleInputChange(event)}
                style={{
                  padding: '8.5px 12px',
                  borderRadius: '4px',
                  borderColor: '#BDBDBD',
                  borderWidth: '1px',
                  borderStyle: 'solid',
                  resize: 'vertical',
                  fontSize: '14px',
                  fontWeight: 400,
                  outline: 'none',
                  width: '100%',
                }}
              />
            </Box>
          </Grid>
        </Grid>
      </Stack>
      <Stack direction={'row'} spacing={'20px'} mt={'32px'}>
        <BtnGradient text='登録' handleButtonClick={handleSave} />
        <BtnOutlineGradient
          text='キャンセル'
          handleButtonClick={handleCancel}
        />
      </Stack>
    </Stack>
  );
};

export default Create;
